import SiteNav from '../components/SiteNav';
import SiteFooter from '../components/SiteFooter';
import ErrorBoundary from '../components/ErrorBoundary';
import usePageMeta, { breadcrumbGraph } from '../hooks/usePageMeta';
import { LANDING_HOME_LINKS } from './templates';
import { useReveal } from './pageShared';
import './About.css';

const ABOUT_URL = 'https://printablegraphpaperpdf.com/about';

const ABOUT_JSONLD = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'AboutPage',
      name: 'About Printable Graph Paper',
      url: ABOUT_URL,
      inLanguage: 'en',
      description:
        'How Printable Graph Paper works — a free, no-signup generator that creates custom graph, dot grid, and isometric paper, ready to download and print.',
      isPartOf: { '@id': 'https://printablegraphpaperpdf.com/' },
    },
    breadcrumbGraph('About', ABOUT_URL),
  ],
};

const PREVIEW_CLASS_BY_PATH = {
  '/a4-graph-paper': 'about-preview-a4',
  '/5mm-graph-paper': 'about-preview-5mm',
  '/dot-grid-paper': 'about-preview-dot',
  '/isometric-graph-paper': 'about-preview-iso',
  '/engineering-graph-paper': 'about-preview-eng',
  '/1-inch-graph-paper': 'about-preview-inch1',
  '/1-4-inch-graph-paper': 'about-preview-quarter',
  '/1-2-inch-graph-paper': 'about-preview-half',
};

const STEPS = [
  {
    num: '01',
    title: 'Choose',
    body: 'Start from a paper type — square, dot, isometric, inch or metric — or open the generator and build from scratch.',
  },
  {
    num: '02',
    title: 'Customize',
    body: 'Set cell size, line weight, color, page size, and orientation. The preview redraws as you adjust.',
  },
  {
    num: '03',
    title: 'Download',
    body: 'Export a crisp PDF, PNG, or SVG at print resolution. Nothing is uploaded and nothing is watermarked.',
  },
  {
    num: '04',
    title: 'Print',
    body: 'Feed it to any printer. Page size and margins are already handled, so the grid lands where it should.',
  },
];

const PRINCIPLES = [
  {
    icon: 'free',
    label: 'Free',
    body: 'Every generator, every format, every download. No account, no trial, and no paywall between you and the print button.',
  },
  {
    icon: 'simple',
    label: 'Simple',
    body: 'The tool is the page. Sensible defaults mean you can print in one click, then change only what matters.',
  },
  {
    icon: 'custom',
    label: 'Customizable',
    body: 'Cell size, line weight, color, page size, and orientation all sit one control away, with a live preview as you adjust.',
  },
  {
    icon: 'print',
    label: 'Print-ready',
    body: 'Sheets are sized to real paper with clean margins, so the grid you preview is the grid that lands on the page.',
  },
];

const PRINCIPLE_ICONS = {
  free: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="4.5" y="10.5" width="15" height="9.5" rx="2" />
      <path d="M8 10.5V7a4 4 0 0 1 7.7-1.5" />
    </svg>
  ),
  simple: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M6 3.5l12.4 7.2-5.5 1.6-1.6 5.5z" />
    </svg>
  ),
  custom: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 8h16M4 16h16" />
      <circle cx="9.5" cy="8" r="2.3" />
      <circle cx="15" cy="16" r="2.3" />
    </svg>
  ),
  print: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M7 9V4h10v5" />
      <rect x="4" y="9" width="16" height="7.5" rx="1.6" />
      <path d="M7 14h10v6H7z" />
    </svg>
  ),
};

const SHEET_V_LINES = Array.from({ length: 11 }, (_, i) => 40 + i * 16);
const SHEET_H_LINES = Array.from({ length: 15 }, (_, i) => 24 + i * 16);

function SpecDiagram() {
  return (
    <figure className="about-spec" data-reveal="true">
      <svg
        className="about-spec-svg"
        viewBox="0 0 240 292"
        role="img"
        aria-label="Diagram of an A4 sheet with a 5 millimetre grid and page dimensions"
      >
        <rect className="spec-sheet" x="40" y="24" width="160" height="224" rx="4" />
        <rect className="spec-highlight" x="40" y="24" width="48" height="48" />
        <g className="spec-grid">
          {SHEET_V_LINES.map((x) => (
            <line key={'v' + x} x1={x} y1="24" x2={x} y2="248" />
          ))}
          {SHEET_H_LINES.map((y) => (
            <line key={'h' + y} x1="40" y1={y} x2="200" y2={y} />
          ))}
        </g>
        <g className="spec-dim">
          <line x1="40" y1="266" x2="56" y2="266" />
          <line x1="40" y1="261" x2="40" y2="271" />
          <line x1="56" y1="261" x2="56" y2="271" />
          <line x1="214" y1="24" x2="214" y2="248" />
          <line x1="209" y1="24" x2="219" y2="24" />
          <line x1="209" y1="248" x2="219" y2="248" />
        </g>
        <text className="spec-label" x="48" y="284" textAnchor="middle">5 mm</text>
        <text
          className="spec-label"
          x="228"
          y="136"
          textAnchor="middle"
          transform="rotate(-90 228 136)"
        >
          297 mm
        </text>
      </svg>
      <figcaption className="about-spec-legend">
        <span className="about-spec-chip">5 mm cells</span>
        <span className="about-spec-chip">A4 · 210 × 297 mm</span>
        <span className="about-spec-chip">PDF · PNG · SVG</span>
      </figcaption>
    </figure>
  );
}

function About() {
  usePageMeta({
    title: 'About Printable Graph Paper | Free Graph Paper Generator',
    description:
      'Learn how Printable Graph Paper works — a free, no-signup generator that creates custom graph, dot grid, and isometric paper, ready to download and print.',
    keywords: 'about, printable graph paper, free graph paper generator, grid paper online, graph paper maker',
    canonical: 'https://printablegraphpaperpdf.com/about',
    ogTitle: 'About Printable Graph Paper | Free Graph Paper Generator',
    ogDescription:
      'A free, no-signup generator that creates custom graph, dot grid, and isometric paper, ready to download and print.',
    ogUrl: 'https://printablegraphpaperpdf.com/about',
    jsonLd: ABOUT_JSONLD,
  });

  const revealRef = useReveal();

  return (
    <ErrorBoundary>
      <div className="app about-page" ref={revealRef}>
        <SiteNav />

        <main className="about-main">
          <header className="about-hero">
            <div className="lp-container about-hero-grid">
              <div className="about-hero-text">
                <p className="lp-eyebrow about-rise about-d1">About</p>
                <h1 className="lp-title about-rise about-d2">Making printable graph paper simple.</h1>
                <p className="lp-lead about-rise about-d3">
                  Printable Graph Paper turns a blank page into exactly the grid you need — pick a
                  type, tune the spacing, and download a sheet that is ready for the printer.
                </p>
                <p className="lp-lead about-rise about-d3">
                  Free, no sign-up, no watermark. Everything runs right in your browser.
                </p>
                <div className="lp-cta-row about-rise about-d4">
                  <a className="lp-btn-primary" href="/">Create graph paper</a>
                  <a className="lp-btn-secondary" href="#about-what">How it works</a>
                </div>
              </div>

              <div className="about-hero-visual about-rise about-d3" aria-hidden="true">
                <div className="about-sheet about-sheet-back">
                  <div className="about-sheet-face about-pattern-quarter" />
                  <span className="about-sheet-label">1/4 in</span>
                </div>
                <div className="about-sheet about-sheet-mid">
                  <div className="about-sheet-face about-pattern-iso" />
                  <span className="about-sheet-label">Isometric</span>
                </div>
                <div className="about-sheet about-sheet-front">
                  <div className="about-sheet-face about-pattern-dot" />
                  <span className="about-sheet-label">Dot grid · 5 mm</span>
                </div>
                <div className="about-hero-chip">PNG · PDF · SVG</div>
              </div>
            </div>
          </header>

          <section className="lp-section about-what" id="about-what">
            <div className="lp-container about-what-grid">
              <div className="about-what-left" data-reveal="true">
                <p className="lp-eyebrow">The basics</p>
                <h2 className="lp-h2">What is graph paper?</h2>
                <p className="about-what-note">Also known as grid paper, squared paper, quadrille paper</p>
                <div className="about-what-copy">
                  <p>
                    A regular grid turns an empty page into a measuring tool. Every cell is a fixed
                    unit, so spacing, scale, and proportion stay consistent without doing the
                    arithmetic by hand.
                  </p>
                  <p>
                    It has quietly powered classrooms, studios, and engineering desks for
                    generations: plotting equations, laying out drawings, planning gardens, mapping
                    pixel art, and drafting anything that needs to line up.
                  </p>
                  <p>
                    Traditionally you bought a pad with whatever spacing the shop happened to stock.
                    Here the pad is the settings panel — choose a spacing, a line weight, and a page
                    size, and the sheet is generated for you.
                  </p>
                </div>
              </div>

              <div className="about-what-right" data-reveal="true">
                <SpecDiagram />
              </div>
            </div>
          </section>

          <section className="lp-section lp-section-tint">
            <div className="lp-container">
              <div className="lp-section-head" data-reveal="true">
                <p className="lp-eyebrow">Why we built it</p>
                <h2 className="lp-h2">The right grid shouldn't be hard to find.</h2>
                <p className="lp-sec-lead">
                  Pads in a shop come with one spacing, one page size, and a price tag. We wanted
                  the opposite — any grid, any page, free and instant — so the whole tool is built
                  around four steps.
                </p>
              </div>

              <ol className="about-steps">
                {STEPS.map((s, i) => (
                  <li
                    className="about-step"
                    key={s.num}
                    data-reveal="true"
                    style={{ transitionDelay: i * 70 + 'ms' }}
                  >
                    <span className="about-step-num">{s.num}</span>
                    <h3 className="about-step-title">{s.title}</h3>
                    <p className="about-step-body">{s.body}</p>
                  </li>
                ))}
              </ol>
            </div>
          </section>

          <section className="lp-section about-types-section" id="about-types">
            <div className="lp-container">
              <div className="lp-section-head" data-reveal="true">
                <p className="lp-eyebrow">Paper types</p>
                <h2 className="lp-h2">What you can create</h2>
                <p className="lp-sec-lead">
                  Eight dedicated generators, each preconfigured to a real paper standard. Open one
                  as-is, or change anything before you print.
                </p>
              </div>

              <div className="about-types-grid">
                {LANDING_HOME_LINKS.map((l, i) => (
                  <a
                    className="about-type-card"
                    href={l.path}
                    key={l.path}
                    data-reveal="true"
                    style={{ transitionDelay: (i % 4) * 60 + 'ms' }}
                  >
                    <span
                      className={'about-type-preview ' + (PREVIEW_CLASS_BY_PATH[l.path] || '')}
                      aria-hidden="true"
                    />
                    <div className="about-type-body">
                      <h3 className="about-type-title">{l.title}</h3>
                      <span className="about-type-blurb">{l.blurb}</span>
                      <span className="about-type-cta">Open generator →</span>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </section>

          <section className="lp-section lp-section-tint">
            <div className="lp-container">
              <div className="lp-section-head" data-reveal="true">
                <p className="lp-eyebrow">Principles</p>
                <h2 className="lp-h2">What we optimize for</h2>
                <p className="lp-sec-lead">
                  Four rules shape every choice in the tool — from the default spacing to the size
                  of the print button.
                </p>
              </div>

              <ul className="about-principles">
                {PRINCIPLES.map((p, i) => (
                  <li
                    className="about-principle"
                    key={p.label}
                    data-reveal="true"
                    style={{ transitionDelay: i * 70 + 'ms' }}
                  >
                    <span className="about-principle-icon">{PRINCIPLE_ICONS[p.icon]}</span>
                    <h3 className="about-principle-title">{p.label}</h3>
                    <p className="about-principle-body">{p.body}</p>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          <section className="about-statement">
            <div className="about-statement-inner" data-reveal="true">
              <h2 className="about-statement-title">From an empty page to the right grid.</h2>
              <p className="about-statement-sub">
                One tool, one job: hand you the exact sheet you had in mind — then get out of the
                way.
              </p>
            </div>
          </section>

          <section className="lp-section about-cta">
            <div className="lp-container">
              <div className="lp-section-head" data-reveal="true">
                <p className="lp-eyebrow">Get started</p>
                <h2 className="lp-h2">Ready to create your own graph paper?</h2>
                <p className="lp-sec-lead">
                  Start from a ready-made type or a blank sheet — you'll be at the print dialog in
                  under a minute.
                </p>
              </div>
              <div className="lp-cta-row" data-reveal="true">
                <a className="lp-btn-primary" href="/">Create graph paper</a>
                <a className="lp-btn-secondary" href="#about-types">Browse paper types</a>
              </div>
            </div>
          </section>
        </main>

        <SiteFooter />
      </div>
    </ErrorBoundary>
  );
}

export default About;
